import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Badge, useToast, Icon, Chip, FieldLabel, clickable } from '../ui';

// GlobalContactPicker — links company-level contacts to a deal or deal detail.
// Props: linkedContacts, dealId, dealDetailId?, onChanged
export const GlobalContactPicker = ({ linkedContacts, dealId, dealDetailId, onChanged }) => {
  const { toast } = useToast();
  const { data: allContacts, reload: reloadAll } = useApi(() => api.get('contacts'), []);
  const [query,   setQuery]   = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ companyName: '', primaryPerson: { name: '', phone: '', email: '', title: '' } });
  const [saving,  setSaving]  = useState(false);
  const [open,    setOpen]    = useState(false);

  const linkedIds = new Set((linkedContacts || []).map(c => c.contactID));

  const filtered = (allContacts || []).filter(c =>
    !linkedIds.has(c.contactID) &&
    (!query.trim() || [c.companyName, c.primaryPersonName, c.primaryPersonEmail].some(f => f?.toLowerCase().includes(query.toLowerCase())))
  );

  const linkPayload = { dealId, dealDetailId: dealDetailId || null };

  const link = async (contact) => {
    try {
      await api.post(`contacts/${contact.contactID}/link`, linkPayload);
      setQuery(''); setOpen(false);
      if (onChanged) await onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const unlink = async (contact) => {
    try {
      await api.del(`contacts/${contact.contactID}/link/${contact.linkId}`);
      if (onChanged) await onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const createAndLink = async () => {
    if (!newForm.companyName.trim()) return;
    setSaving(true);
    try {
      const created = await api.post('contacts', {
        companyName:   newForm.companyName.trim(),
        primaryPerson: newForm.primaryPerson.name?.trim() ? newForm.primaryPerson : null,
      });
      await api.post(`contacts/${created.contactID}/link`, linkPayload);
      setNewForm({ companyName: '', primaryPerson: { name: '', phone: '', email: '', title: '' } });
      setShowNew(false);
      await reloadAll();
      if (onChanged) await onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 8 }}>
      {(linkedContacts || []).length === 0 && !open && !showNew && (
        <div style={{ fontSize: 13, color: B.textLt, padding: '8px 0 12px' }}>No contacts linked yet.</div>
      )}
      {(linkedContacts || []).map(c => (
        <div key={c.contactID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${B.border}` }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{c.companyName} {c.isPrimary && <Badge color={B.navy}>Primary</Badge>}</div>
            {c.primaryPersonName && (
              <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>{c.primaryPersonName}{c.primaryPersonTitle ? ` · ${c.primaryPersonTitle}` : ''}</div>
            )}
            {(c.primaryPersonPhone || c.primaryPersonEmail) && (
              <div style={{ fontSize: 12, color: B.textLt }}>{[c.primaryPersonPhone, c.primaryPersonEmail].filter(Boolean).join(' · ')}</div>
            )}
          </div>
          <Btn size="sm" variant="ghost" onClick={() => unlink(c)}>Unlink</Btn>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type="text" value={query} placeholder="Search companies to link…"
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${B.border}`, borderRadius: 6,
              padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
          />
          {open && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
              background: B.white, border: `1px solid ${B.border}`, borderRadius: 8,
              boxShadow: B.shadowMd, maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
              {filtered.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: 12, color: B.textLt }}>
                  {query ? 'No matches — create a new company below.' : 'Type to search…'}
                </div>
              )}
              {filtered.map(c => (
                <div key={c.contactID} {...clickable(() => link(c), { role: 'option' })}
                  style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${B.border}` }}
                  onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontWeight: 600 }}>{c.companyName}</div>
                  {c.primaryPersonName && (
                    <div style={{ fontSize: 11, color: B.textLt }}>{c.primaryPersonName}{c.primaryPersonEmail ? ` · ${c.primaryPersonEmail}` : ''}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <Btn size="sm" variant="secondary" onClick={() => setShowNew(v => !v)}>
          {showNew ? 'Cancel' : '+ New Company'}
        </Btn>
      </div>

      {showNew && (
        <div style={{ background: B.surfaceMuted, borderRadius: 8, padding: 14, marginTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>New Company</div>
          <input value={newForm.companyName}
            onChange={e => setNewForm(p => ({ ...p, companyName: e.target.value }))}
            placeholder="Company name *" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8,
              border: `1px solid ${B.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          <div style={{ fontSize: 11, fontWeight: 600, color: B.textMd, marginBottom: 6 }}>Primary Contact (optional)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <input value={newForm.primaryPerson.name}
              onChange={e => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, name: e.target.value } }))}
              placeholder="Full name" style={{ border: `1px solid ${B.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
            <input value={newForm.primaryPerson.title}
              onChange={e => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, title: e.target.value } }))}
              placeholder="Title" style={{ border: `1px solid ${B.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
            <input value={newForm.primaryPerson.email} type="email"
              onChange={e => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, email: e.target.value } }))}
              placeholder="Email" style={{ border: `1px solid ${B.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
            <input value={newForm.primaryPerson.phone} type="tel"
              onChange={e => setNewForm(p => ({ ...p, primaryPerson: { ...p.primaryPerson, phone: e.target.value } }))}
              placeholder="Phone" style={{ border: `1px solid ${B.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }} />
          </div>
          <Btn size="sm" disabled={!newForm.companyName.trim() || saving} onClick={createAndLink}>
            {saving ? 'Saving…' : 'Create & Link'}
          </Btn>
        </div>
      )}
    </div>
  );
};

// HarvusLeadMultiSelect — chip-style multi-select for internal users
// value: array of userIDs  |  onChange(newArray)
// embedded: render the options list in normal flow (for use inside small popups
//           that size to their content) rather than as an absolute overlay.
export const HarvusLeadMultiSelect = ({ users, value, onChange, label = "Harvus Lead", embedded = false }) => {
  const internalUsers = (users || []).filter(u => u.isInternal && u.isActive !== false);
  const selected = internalUsers.filter(u => (value || []).includes(u.userID));
  const unselected = internalUsers.filter(u => !(value || []).includes(u.userID));
  const [open, setOpen] = useState(embedded);

  const firstName = name => (name || '').split(' ')[0];

  const add    = id  => onChange([...(value || []), id]);
  const remove = id  => onChange((value || []).filter(v => v !== id));

  return (
    <div style={{ marginBottom: 14, position: 'relative' }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 36,
        border: `1px solid ${open ? B.navy : B.border}`, borderRadius: 6, padding: '5px 8px',
        background: B.white, cursor: 'pointer', position: 'relative' }}
        onClick={() => !embedded && setOpen(v => !v)}>
        {selected.length === 0 && (
          <span style={{ fontSize: 13, color: B.textLt }}>— None selected —</span>
        )}
        {selected.map(u => (
          <Chip key={u.userID} color={B.navy} onRemove={() => remove(u.userID)}>
            {firstName(u.displayName)}
          </Chip>
        ))}
        <span style={{ marginLeft: 'auto', color: B.textLt, display: 'inline-flex' }}><Icon name="chevronDown" size={14} /></span>
      </div>
      {open && (
        <div style={embedded ? {
          position: 'relative', background: B.white,
          border: `1px solid ${B.border}`, borderRadius: 8,
          maxHeight: 200, overflowY: 'auto', marginTop: 6,
        } : {
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300, background: B.white,
          border: `1px solid ${B.border}`, borderRadius: 8, boxShadow: B.shadowMd,
          maxHeight: 200, overflowY: 'auto', marginTop: 2,
        }} onMouseLeave={() => !embedded && setOpen(false)}>
          {unselected.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: B.textLt }}>All leads selected</div>
          )}
          {unselected.map(u => (
            <div key={u.userID} {...clickable((e) => { e.stopPropagation(); add(u.userID); }, { role: 'option' })}
              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${B.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {u.displayName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

